/**
 * Category Definition Service
 *
 * Handles fetching and caching category definitions from app.category_definitions
 *
 * @module CategoryDefinitionService
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { APP_SCHEMA_ENDPOINTS, buildUrlWithParams } from '../api';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import type { CategoryDefinitionRead } from '../models/category-definition.types';

@Injectable({
  providedIn: 'root',
})
export class CategoryDefinitionService {
  private http = inject(HttpClient);

  // Signals for state management
  readonly addressCategories = signal<CategoryDefinitionRead[]>([]);
  readonly sideAddressCategories = signal<CategoryDefinitionRead[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  private createHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  private handleError = (error: unknown): Observable<never> => {
    console.error('❌ CategoryDefinitionService Error:', error);
    this.error.set('Fehler beim Laden der Kategorien');
    throw error;
  };

  /**
   * Get all address categories (component = 'address_category')
   */
  getAddressCategories(): Observable<CategoryDefinitionRead[]> {
    this.loading.set(true);
    this.error.set(null);

    const url = buildUrlWithParams(`${APP_SCHEMA_ENDPOINTS.categoryDefinitions.list}`, {
      component: 'address_category',
    });

    return this.http.get<CategoryDefinitionRead[]>(url, { headers: this.createHeaders() }).pipe(
      tap((categories) => {
        this.addressCategories.set(categories);
        this.loading.set(false);
      }),
      catchError((error) => {
        this.loading.set(false);
        return this.handleError(error);
      })
    );
  }

  /**
   * Get all side address categories (component = 'side_address_category')
   * Uses specialized backend endpoint with proper server-side filtering
   */
  getSideAddressCategories(): Observable<CategoryDefinitionRead[]> {
    this.loading.set(true);
    this.error.set(null);

    // Use specialized endpoint that properly filters by component
    const url = `${APP_SCHEMA_ENDPOINTS.categories.base}/side-address-categories`;

    return this.http.get<CategoryDefinitionRead[]>(url, { headers: this.createHeaders() }).pipe(
      tap((categories) => {
        this.sideAddressCategories.set(categories);
        this.loading.set(false);
      }),
      catchError((error) => {
        this.loading.set(false);
        return this.handleError(error);
      })
    );
  }

  /**
   * Get category name by ID
   */
  getCategoryNameById(categoryId: number | null): string {
    if (!categoryId) return '-';
    const category = this.sideAddressCategories().find((c) => c.category_id === categoryId);
    return category?.description || '-';
  }

  /**
   * Get all categories (without filter)
   */
  getAllCategories(): Observable<CategoryDefinitionRead[]> {
    this.loading.set(true);
    this.error.set(null);

    const url = buildUrlWithParams(APP_SCHEMA_ENDPOINTS.categoryDefinitions.list);

    return this.http.get<CategoryDefinitionRead[]>(url, { headers: this.createHeaders() }).pipe(
      tap(() => {
        this.loading.set(false);
      }),
      catchError((error) => {
        this.loading.set(false);
        return this.handleError(error);
      })
    );
  }
}
